import type { PosterAssetDto } from "../../api/posterService";

/**
 * 分段在整图里的纵向位置(2026-08-07 版面重构 P1-c)。
 *
 * 长图成品是分段**纯纵向拼接**:实测任务 4908234834944 —— 7 段
 * 2000+844+2000+2000+320+320+1500 = 8984,与整图高度 **一个像素不差**,
 * 且每段等宽(1500)。所以「点右边某一块 → 左边跳到那一块」是算得准的,不是估。
 *
 * ★但仍然要**当场自证**:老任务/将来改了拼接策略(加间距、加页眉)都会让这条不成立。
 * 对不上就返回 null —— 上层据此把跳转入口收掉,而不是跳到一个错位置。
 * 跳错位比不能跳更糟:用户点「规格参数」看到的是别的块,他不会怀疑是跳错了,
 * 只会以为图拼错了。
 */
export interface SegmentOffsets {
  /** blockId → 该块顶边在整图中的位置(0~1)。 */
  ratioOf: Map<string, number>;
  /** 按顺序的 [blockId, 顶边比例, 底边比例]。 */
  bands: { blockId: string; top: number; bottom: number }[];
}

/** 允许的累计误差(相对整图高度)。超过就判「对不上」。 */
const TOLERANCE = 0.02;

export function segmentOffsets(
  fullHeight: number | null | undefined,
  segments: Pick<PosterAssetDto, "blockId" | "actualHeight">[]
): SegmentOffsets | null {
  if (!fullHeight || fullHeight <= 0) return null;
  if (segments.length === 0) return null;
  // 任一段缺高度 → 整条链断了,不猜
  if (segments.some((s) => !s.blockId || !s.actualHeight || s.actualHeight <= 0)) return null;

  const sum = segments.reduce((n, s) => n + (s.actualHeight ?? 0), 0);
  if (Math.abs(sum - fullHeight) / fullHeight > TOLERANCE) return null;

  const ratioOf = new Map<string, number>();
  const bands: SegmentOffsets["bands"] = [];
  let acc = 0;
  for (const s of segments) {
    const top = acc / sum;
    acc += s.actualHeight ?? 0;
    const bottom = acc / sum;
    ratioOf.set(s.blockId as string, top);
    bands.push({ blockId: s.blockId as string, top, bottom });
  }
  return { ratioOf, bands };
}

/**
 * 边界容差。
 *
 * ★不是玄学常数,是**浏览器把 scrollTop 取整到整像素**这件事的直接后果:
 *   真页面实测(1500×8984 的长图,渲成 5091px 高)—— 点「规格参数」跳到它的顶边
 *   `0.7617988 × 5091 = 3878.14`,浏览器落在 **3878**,回报 `3878/5091 = 0.7617364`,
 *   比块顶低 6.2e-5 → 「跳对了位置,右边亮的却是上一块」。
 * ★0.002 ≈ 5000px 渲染下的 10px,远小于最小的一块(320/8984 = 3.6%),
 *   所以不会跨块误判;手动滚到边界附近时它会提前亮下一块,那正是「马上要读到它了」。
 */
const BOUNDARY_TOLERANCE = 0.002;

/** 当前位置落在哪一块上(band 内即命中;取最后一个 top ≤ r 的块)。 */
export function activeBlockAt(
  offsets: SegmentOffsets | null,
  ratio: number,
  tolerance = BOUNDARY_TOLERANCE
): string | null {
  if (!offsets) return null;
  let hit: string | null = null;
  for (const b of offsets.bands) {
    if (ratio >= b.top - tolerance) hit = b.blockId;
    else break;
  }
  return hit;
}
